/**
 * Adobe IMS OAuth client (Authorization Code + PKCE, BFF token exchange).
 *
 * Hits the prod IMS environment. The IMS client we use
 * (`adobe-llm-provider-monitoring-web`) is a confidential web client, so
 * the token exchange step requires a `client_secret`. We don't ship that
 * secret to the browser — instead the SPA posts the authorisation code +
 * PKCE verifier to the worker's `/v1/auth/exchange` endpoint, which holds
 * the secret server-side and proxies the call to /ims/token/v3.
 *
 * Access tokens live in localStorage; silent re-auth via `prompt=none`
 * covers most short-lived expiries because users stay signed into Adobe
 * SSO across internal tools.
 */

const IMS_BASE = 'https://ims-na1.adobelogin.com';
const CLIENT_ID = 'adobe-llm-provider-monitoring-web';
const SCOPES = 'openid AdobeID email profile additional_info.roles';
const PKCE_STORAGE_KEY = 'ims-pkce-state';
const WORKER_BASE =
  import.meta.env.VITE_API_URL || 'https://llm-monitoring-worker.paolo-moz.workers.dev';

function getRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

interface PkceState {
  verifier: string;
  state: string;
  returnTo: string;
}

interface WorkerExchangeResult {
  accessToken: string;
  idToken?: string;
  expiresIn: number;
  tokenType?: string;
  profile?: {
    email?: string;
    name?: string;
    picture?: string;
  };
  /** Set by the worker based on UNMASKED_EMAIL_VIEWERS env var. Cosmetic only. */
  canViewFullEmail?: boolean;
}

export interface AuthSession {
  accessToken: string;
  /** Wall-clock ms epoch when the token expires (already minus a 60s skew buffer). */
  expiresAt: number;
  idToken?: string;
  email?: string;
  name?: string;
  /** OIDC `picture` claim if Adobe IMS populates it; otherwise undefined. */
  picture?: string;
  /**
   * Cosmetic hint from the worker (UNMASKED_EMAIL_VIEWERS env var).
   * When false the worker returns hashed userIds; the SPA hides email-search UI.
   * The worker enforces masking server-side regardless of this flag.
   */
  canViewFullEmail?: boolean;
}

/**
 * Encode an arbitrary byte array as base64url (no padding) — the
 * encoding PKCE and JWT both expect.
 */
function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function generateVerifier(): string {
  // 64 random bytes → 86 base64url chars, well within PKCE's 43–128 range.
  const bytes = new Uint8Array(64);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function deriveChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(hash));
}

function generateState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/**
 * Decode a JWT payload without verifying its signature. We use this
 * only to read non-security-critical claims (email, name) from the
 * id_token — IMS already validated the token by issuing it. Trust
 * decisions still rely on the worker's server-side `/ims/profile/v1`
 * lookup against the access token.
 */
function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    const payloadPart = parts[1];
    if (!payloadPart) return null;
    const padded = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padLen = (4 - (padded.length % 4)) % 4;
    const json = atob(padded + '='.repeat(padLen));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Kick off an OAuth Authorization Code + PKCE redirect to IMS.
 *
 * `returnTo` is the in-app path the user should land on after the
 * round-trip completes (defaults to `/`).
 *
 * `prompt: 'none'` is used for silent re-auth when an access token
 * expires but the underlying SSO is still alive — IMS bounces back
 * instantly without user interaction.
 */
export async function startSignIn(
  returnTo = '/',
  prompt?: 'none' | 'login',
): Promise<void> {
  const verifier = generateVerifier();
  const challenge = await deriveChallenge(verifier);
  const state = generateState();

  const pkce: PkceState = { verifier, state, returnTo };
  sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify(pkce));

  const url = new URL(`${IMS_BASE}/ims/authorize/v2`);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('redirect_uri', getRedirectUri());
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', SCOPES);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);
  if (prompt) url.searchParams.set('prompt', prompt);

  window.location.assign(url.toString());
}

export interface CallbackResult {
  session: AuthSession;
  returnTo: string;
}

/**
 * Handle the OAuth callback — exchange the authorization code for an
 * access token using the PKCE verifier we stashed at sign-in. Throws
 * with a human-readable message on any failure (CSRF mismatch, IMS
 * error response, missing PKCE state, etc.).
 */
export async function completeCallback(): Promise<CallbackResult> {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  if (error) {
    const desc = params.get('error_description') ?? error;
    throw new Error(`IMS rejected sign-in: ${desc}`);
  }

  const code = params.get('code');
  const returnedState = params.get('state');
  if (!code) throw new Error('Missing authorization code on callback.');
  if (!returnedState) throw new Error('Missing state on callback.');

  const raw = sessionStorage.getItem(PKCE_STORAGE_KEY);
  if (!raw) {
    throw new Error('Missing PKCE state — sign-in flow was not initiated from this tab.');
  }
  let pkce: PkceState;
  try {
    pkce = JSON.parse(raw) as PkceState;
  } catch {
    sessionStorage.removeItem(PKCE_STORAGE_KEY);
    throw new Error('Corrupted PKCE state — please sign in again.');
  }
  if (pkce.state !== returnedState) {
    sessionStorage.removeItem(PKCE_STORAGE_KEY);
    throw new Error('State mismatch — refusing to complete sign-in.');
  }
  sessionStorage.removeItem(PKCE_STORAGE_KEY);

  const res = await fetch(`${WORKER_BASE}/v1/auth/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code,
      codeVerifier: pkce.verifier,
      redirectUri: getRedirectUri(),
    }),
  });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const errJson = (await res.json()) as { error?: { message?: string }; message?: string };
      detail = errJson.error?.message ?? errJson.message ?? detail;
    } catch {
      // ignore — fall back to status code
    }
    throw new Error(`Token exchange failed: ${detail}`);
  }
  const data = (await res.json()) as WorkerExchangeResult;

  // Subtract a 60s skew buffer so we treat the token as expired slightly
  // before IMS does — avoids races where we send a token that the
  // worker sees as already-expired by the time it lands.
  const expiresAt = Date.now() + Math.max(0, data.expiresIn - 60) * 1000;

  // Prefer the worker-fetched IMS profile (it gets `image`/`picture` for
  // Federated IDs, which the id_token usually doesn't include) and fall
  // back to the OIDC id_token claims when the profile lookup yielded
  // nothing.
  let email: string | undefined = data.profile?.email;
  let name: string | undefined = data.profile?.name;
  let picture: string | undefined = data.profile?.picture;
  if ((!email || !name || !picture) && data.idToken) {
    const tokenPayload = decodeJwtPayload(data.idToken);
    if (tokenPayload) {
      if (!email && typeof tokenPayload.email === 'string') email = tokenPayload.email;
      if (!name && typeof tokenPayload.name === 'string') name = tokenPayload.name;
      if (!picture && typeof tokenPayload.picture === 'string')
        picture = tokenPayload.picture;
    }
  }

  return {
    session: {
      accessToken: data.accessToken,
      expiresAt,
      idToken: data.idToken,
      email,
      name,
      picture,
      canViewFullEmail: data.canViewFullEmail ?? false,
    },
    returnTo: pkce.returnTo || '/',
  };
}

/**
 * Build the IMS end-session URL. Calling this redirects the user
 * through IMS to clear the SSO cookie too — most apps just clear
 * local state and let the SSO cookie expire naturally, which is what
 * a click on "Sign out" does today. Exported for completeness.
 */
export function buildLogoutUrl(postLogoutRedirect = window.location.origin): string {
  const url = new URL(`${IMS_BASE}/ims/logout/v1`);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('redirect_uri', postLogoutRedirect);
  return url.toString();
}
